import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './Video.module.scss';
import type { VideoProps, VideoRef } from './Video.data';
import VideoControls from '../../molecules/video-controls/VideoControls';
import useVideoResize from './hooks/useVideoResize';
import useVideoActions from './hooks/useVideoActions';

// import PlayButton from '../play-button/PlayButton';
import type { PlayButtonRef } from '../play-button/PlayButton.data';
import Image from '../image/Image';
import type { ImageRef } from '../image/Image.data';
import { fade } from './Video.transitions';
import VideoPlayer from './video-player/VideoPlayer';
import type { VideoPlayerRef } from './video-player/VideoPlayer.data';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import useUnmount from '../../../hooks/useUnmount';
import { useGlobalStore } from '../../../stores/global';
import { isDesktop } from '../../../hooks/useIsDesktopDevice';
import { useHideOnTimer } from '../../../hooks/useHideOnTimer';
import dynamic from 'next/dynamic';
import { useClientOnly } from '../../../hooks/useClientOnly';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useDocumentVisibilityChange } from '../../../hooks/useDocumentVisibilityChange';

const PlayButton = dynamic(() => import('../play-button/PlayButton'));
/*
 * NOTE : For Vimeo videos, hiding controls must be enabled by the video owner
 * Doc reference : https://github.com/CookPete/react-player
 */
export default function Video({
  controlsVisible = true,
  className,
  blok,
  width = '100%',
  height = '100%',
  objectFit = 'cover',
  controls = false,
  volume = 1,
  muted = true,
  playing = false,
  isFullScreen = false,
  playButton = false,
  removeOnPause = true,
  onPlayStateChange,
  imageSizes,
  innerTransition,
  playsinline = true,
  loop,
  playerType,
  exitFullScreen,
  enableFullscreenButton = true,
  requireClickListener = false,
  excludePoster = false,
  fadeOutOnPause = true,
  hideControlsTime = 2000,
  openInModal = !isDesktop,
  pauseWhenOutViewport,
  lazyLoad = true,
  onMouseEnter,
  onMouseMove,
  ref,
}: VideoProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<VideoRef | null>)?.current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const playerRef = useRef<VideoPlayerRef>(null);
  const videoHolderRef = useRef<HTMLDivElement>(null);
  const playButtonRef = useRef<PlayButtonRef>(null);
  const posterImageRef = useRef<ImageRef>(null);
  const [renderPlayer, setRenderPlayer] = useState(false);
  const isClientOnly = useClientOnly();
  const [renderWhenInView, setRenderWhenInView] = useState(!lazyLoad);

  let filePath = blok?.vimeo_url;
  let isIframe = true;
  if (blok?.hosted_video && blok?.hosted_video?.filename?.length > 0) {
    isIframe = false;
    filePath = blok?.hosted_video?.filename;
  }

  const isVisibleInViewport = useIntersectionObserver(
    elementRef,
    {
      threshold: [0, 1],
    },
    pauseWhenOutViewport || lazyLoad,
  );

  useEffect(() => {
    if (isVisibleInViewport) {
      setRenderWhenInView(isVisibleInViewport);
    }
  }, [isVisibleInViewport]);

  const { updateVideoSize } = useVideoResize(elementRef, videoHolderRef, objectFit, isIframe);
  const {
    forcePlay,
    forceMute,
    handleOnDuration,
    handleOnProgress,
    handleOnEnded,
    handleOnPlay,
    handleOnPause,
    progress,
    isPlaying,
    duration,
    onProgressChange,
    togglePlay,
    toggleMute,
    toggleFullScreen,
    isFullscreen: isNativeFullScreen,
    play,
    pause,
  } = useVideoActions(playerRef, removeOnPause, playing, muted);

  useEffect(() => {
    if (pauseWhenOutViewport && !isVisibleInViewport) {
      pause();
    }
  }, [pause, isVisibleInViewport, pauseWhenOutViewport]);

  const store = useGlobalStore();
  const fadeTweenRef = useRef<gsap.core.Tween>(undefined);
  const onPlayButtonClick = useCallback(() => {
    if (!isPlaying) {
      if (openInModal) {
        store.openVideoModal({
          video: blok,
        });
      } else {
        play();
      }
    } else {
      pause();
    }
  }, [isPlaying, openInModal, store, blok, play, pause]);

  const [isDocumentHidden, wasActive, setWasActive] = useDocumentVisibilityChange();
  useEffect(() => {
    if (isDocumentHidden && isPlaying) {
      setWasActive(true);
      pause();
    } else if (wasActive && !isDocumentHidden) {
      play();
      setWasActive(false);
    }
  }, [isDocumentHidden, isPlaying, pause, play, setWasActive, wasActive]);

  useEffect(() => {
    if (onPlayStateChange) {
      onPlayStateChange(isPlaying);
    }
  }, [isPlaying, onPlayStateChange]);

  useUnmount(() => {
    if (fadeTweenRef.current) {
      fadeTweenRef.current.kill();
    }
  });

  useUpdateEffect(() => {
    if (posterImageRef.current) {
      setRenderPlayer(true);

      if (fadeTweenRef.current) {
        fadeTweenRef.current.kill();
      }

      fadeTweenRef.current = fade(posterImageRef, !isPlaying);
      fadeTweenRef.current.then(() => {
        if (!(playing || isPlaying) && fadeOutOnPause) {
          setRenderPlayer(false);
        }
      });
    }
  }, [forcePlay, playing, posterImageRef, isPlaying]);

  const handleOnReady = useCallback(() => {
    // hide loader if wanted (showLoader prop orso)
  }, []);

  const handleOnCreated = useCallback(() => {
    // show loader if wanted (showLoader prop orso)
  }, []);

  const [hideControlsOnTimer, hideOnTimerMouseEnter, hideOnTimerMouseMove] =
    useHideOnTimer(hideControlsTime);

  const onContainerMouseEnter = useCallback(() => {
    hideOnTimerMouseEnter();
    onMouseEnter?.();
  }, [hideOnTimerMouseEnter, onMouseEnter]);

  const onContainerMouseMove = useCallback(() => {
    hideOnTimerMouseMove();
    onMouseMove?.();
  }, [hideOnTimerMouseMove, onMouseMove]);

  useImperativeHandle(
    ref,
    () => ({
      videoHolderRef,
      playerRef,
      isPlaying,
      elementRef,
      posterImageRef,
      updateVideoSize,
    }),
    [playerRef, elementRef, videoHolderRef, isPlaying, posterImageRef, updateVideoSize],
  );

  return (
    <div
      ref={elementRef}
      className={classNames(styles.videoWrapper, className, {
        [styles.isCovered]: objectFit === 'cover',
        [styles.isContained]: objectFit === 'contain',
      })}
      onMouseEnter={onContainerMouseEnter}
      onMouseMove={onContainerMouseMove}
    >
      <div className={styles.allowScrollOnSafari} />
      <div ref={videoHolderRef} className={classNames(styles.videoHolder)}>
        {renderWhenInView &&
          (!removeOnPause || (removeOnPause && (isPlaying || forcePlay || renderPlayer))) &&
          !openInModal && (
            <VideoPlayer
              ref={playerRef}
              playing={forcePlay}
              playerType={playerType}
              playsinline={playsinline}
              loop={loop}
              volume={volume}
              muted={forceMute}
              onProgress={handleOnProgress}
              onDuration={handleOnDuration}
              url={filePath}
              width={width}
              height={height}
              onEnded={handleOnEnded}
              onPlay={handleOnPlay}
              onReady={handleOnReady}
              onCreated={handleOnCreated}
              onPause={handleOnPause}
              requireClickListener={requireClickListener}
              controls={false}
              className={classNames(styles.videoPlayer)}
            />
          )}
      </div>

      {/* Show Controls */}
      {isClientOnly && controls && !openInModal && (
        <VideoControls
          progress={progress}
          isMuted={forceMute}
          isVisible={{
            onMount: true,
            value: controlsVisible && !hideControlsOnTimer,
          }}
          isPlaying={isPlaying}
          duration={duration}
          onProgressUpdate={onProgressChange}
          onTogglePlay={togglePlay}
          onToggleMute={toggleMute}
          className={classNames(styles.videoControls, {
            [styles.visible]: isPlaying && controlsVisible,
          })}
          onToggleFullScreen={toggleFullScreen}
          isFullscreen={isFullScreen ?? isNativeFullScreen}
          exitFullScreen={exitFullScreen}
          enableFullscreenButton={enableFullscreenButton}
        />
      )}

      {/* Show Poster */}
      {blok?.poster_image?.filename && blok.poster_image.filename.length > 0 && !excludePoster && (
        <Image
          ref={posterImageRef}
          src={blok.poster_image.filename}
          srcDesktop={blok.poster_image.filename}
          layout="fill"
          innerTransition={innerTransition}
          sizes={imageSizes}
          className={classNames(styles.posterImage, 'abs-fill')}
          alt={blok.poster_image.alt || 'video poster'}
        />
      )}

      {/* Show Play Button */}
      {playButton && (
        <PlayButton
          ref={playButtonRef}
          className={classNames(styles.playButton, 'abs-center')}
          onClick={onPlayButtonClick}
          isPlaying={isPlaying}
          disableTriggerInOnScroll={false}
          triggerInOnce
          isVisible={{
            onMount: false,
            value: (isPlaying && !hideControlsOnTimer) || !isPlaying,
          }}
        />
      )}
    </div>
  );
}
